import { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import ImportModal from '../components/ImportModal';
import { useCompany } from '../context/CompanyContext';
import { getCurrentFyStartYear } from '../utils/fy';
import api from '../api';
import './Dashboard.css';
import './DataEntry.css';

const STATUSES = ['Not Started', 'In Progress', 'Completed', 'Filed'];

const STATUS_PILL = {
  'Not Started': 'dash-pill-blue',
  'In Progress': 'dash-pill-amber',
  Completed: 'dash-pill-green',
  Filed: 'dash-pill-green',
};

const EMPTY_FORM = {
  client_id: '',
  name_of_company: '',
  date_of_audit: '',
  status: 'Not Started',
  fees_received: 0,
  remarks: '',
};

const AUDIT_TYPES = ['Internal', 'Stock', 'Statutory', 'Concurrent', 'Other'];
const FREQUENCIES = ['Yearly', 'Quarterly', 'Monthly', 'Other'];
const OCCURRENCE_STATUSES = ['Pending', 'In Progress', 'Completed'];
const RISK_RATINGS = ['High', 'Med', 'Low'];
const RECOMMENDATION_STATUSES = ['Pending', 'In Process', 'Closed'];

const OCC_STATUS_PILL = { Pending: 'dash-pill-amber', 'In Progress': 'dash-pill-blue', Completed: 'dash-pill-green' };
const RISK_PILL = { High: 'dash-pill-red', Med: 'dash-pill-amber', Low: 'dash-pill-blue' };
const REC_STATUS_PILL = { Pending: 'dash-pill-amber', 'In Process': 'dash-pill-blue', Closed: 'dash-pill-green' };

const EMPTY_PLAN_FORM = {
  client_id: '',
  fy_year: getCurrentFyStartYear(),
  audit_type: 'Internal',
  frequency: 'Yearly',
  custom_count: '',
  auditor_name: '',
  remarks: '',
};

const EMPTY_REC_FORM = {
  occurrence_id: '',
  description: '',
  risk_rating: 'Med',
  status: 'Pending',
  due_date: '',
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function AuditStatus() {
  const { selectedCompanyId, selectedCompany } = useCompany();
  const isBusinessOwner = selectedCompany?.client_mode === 'Business Owner';
  const [activeTab, setActiveTab] = useState('log');
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const clientName = (id) => clients.find((c) => c.id === id)?.name_of_client || '—';
  const clientType = (id) => clients.find((c) => c.id === id)?.type_of_client || null;

  // ---------------------------------------------------------------- Log tab
  // (unchanged - the existing simple ad-hoc audit log, kept as its own tab
  // rather than replaced by the richer Plan/Occurrence/Recommendation
  // structure below)

  const [rows, setRows] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const refresh = () => {
    if (!selectedCompanyId) return Promise.resolve();
    return api.get('/audit-status', { params: { company_id: selectedCompanyId } }).then((res) => setRows(res.data));
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      client_id: r.client_id || '',
      name_of_company: r.name_of_company || '',
      date_of_audit: r.date_of_audit || '',
      status: r.status || 'Not Started',
      fees_received: r.fees_received || 0,
      remarks: r.remarks || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedCompanyId) return;
    setSaving(true);
    const payload = {
      ...form,
      company_id: selectedCompanyId,
      client_id: form.client_id ? Number(form.client_id) : null,
      date_of_audit: form.date_of_audit || null,
      fees_received: Number(form.fees_received) || 0,
    };
    const request = editingId ? api.put(`/audit-status/${editingId}`, payload) : api.post('/audit-status', payload);
    request
      .then(() => {
        refresh();
        showToast(editingId ? 'Audit record updated ✓' : 'Audit record added ✓');
        setModalOpen(false);
      })
      .finally(() => setSaving(false));
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    api.delete(`/audit-status/${deleteTarget.id}`).then(() => {
      refresh();
      showToast(`${deleteTarget.name_of_company} deleted`);
      setDeleteTarget(null);
    });
  };

  // --------------------------------------------------------------- Plans tab

  const [plans, setPlans] = useState([]);
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [planForm, setPlanForm] = useState(EMPTY_PLAN_FORM);
  const [planDeleteTarget, setPlanDeleteTarget] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);

  const refreshPlans = () => {
    if (!selectedCompanyId) return Promise.resolve();
    return api.get('/audit-plans', { params: { company_id: selectedCompanyId } }).then((res) => setPlans(res.data));
  };

  const planLabel = (plan) => `${plan.audit_type} - ${plan.frequency} - FY${plan.fy_year}-${String(plan.fy_year + 1).slice(2)}`;

  const openAddPlan = () => {
    setEditingPlanId(null);
    setPlanForm(EMPTY_PLAN_FORM);
    setPlanModalOpen(true);
  };

  const openEditPlan = (p) => {
    setEditingPlanId(p.id);
    setPlanForm({
      client_id: p.client_id || '',
      fy_year: p.fy_year,
      audit_type: p.audit_type,
      frequency: p.frequency,
      custom_count: p.custom_count || '',
      auditor_name: p.auditor_name || '',
      remarks: p.remarks || '',
    });
    setPlanModalOpen(true);
  };

  const handlePlanSubmit = (e) => {
    e.preventDefault();
    if (!selectedCompanyId) return;
    setSavingPlan(true);
    const payload = {
      company_id: selectedCompanyId,
      client_id: planForm.client_id ? Number(planForm.client_id) : null,
      fy_year: Number(planForm.fy_year),
      audit_type: planForm.audit_type,
      frequency: planForm.frequency,
      custom_count: planForm.frequency === 'Other' ? Number(planForm.custom_count) || null : null,
      auditor_name: planForm.auditor_name || null,
      remarks: planForm.remarks || null,
    };
    const request = editingPlanId ? api.put(`/audit-plans/${editingPlanId}`, payload) : api.post('/audit-plans', payload);
    request
      .then(() => {
        refreshPlans();
        refreshOccurrences();
        showToast(editingPlanId ? 'Audit plan updated ✓' : 'Audit plan added - occurrences generated ✓');
        setPlanModalOpen(false);
      })
      .catch((err) => showToast(err.response?.data?.detail || 'Could not save plan'))
      .finally(() => setSavingPlan(false));
  };

  const confirmDeletePlan = () => {
    if (!planDeleteTarget) return;
    api.delete(`/audit-plans/${planDeleteTarget.id}`).then(() => {
      refreshPlans();
      refreshOccurrences();
      refreshRecommendations();
      showToast('Audit plan and its occurrences deleted');
      setPlanDeleteTarget(null);
    });
  };

  // --------------------------------------------------------- Occurrences tab

  const [occurrences, setOccurrences] = useState([]);
  const [occFilterPlan, setOccFilterPlan] = useState('');
  const [occModalItem, setOccModalItem] = useState(null);
  const [savingOcc, setSavingOcc] = useState(false);

  const refreshOccurrences = (planFilter) => {
    if (!selectedCompanyId) return Promise.resolve();
    const params = { company_id: selectedCompanyId };
    const pf = planFilter !== undefined ? planFilter : occFilterPlan;
    if (pf) params.plan_id = pf;
    return api.get('/audit-occurrences', { params }).then((res) => setOccurrences(res.data));
  };

  const planById = (id) => plans.find((p) => p.id === id);

  // Pulls from the manually-entered counts on the occurrence itself (see
  // "Recommendation Counts by Audit Report" on the Recommendations tab) -
  // NOT from individual AuditRecommendation rows, so this always agrees
  // with what's shown/edited there rather than a second, competing count.
  const recCountsForOccurrence = (occ) => {
    const { recommendations_given: given, recommendations_closed: closed, recommendations_open: open, recommendations_in_process: inProcess } = occ;
    if (given === null && closed === null && open === null && inProcess === null) return null;
    return {
      total: given !== null && given !== undefined ? given : (closed || 0) + (open || 0) + (inProcess || 0),
      pending: open || 0,
      inProcess: inProcess || 0,
      closed: closed || 0,
    };
  };

  const openEditOcc = (o) => {
    setOccModalItem({
      id: o.id,
      status: o.status,
      completion_date: o.completion_date || '',
      report_submitted: o.report_submitted,
    });
  };

  const handleOccSubmit = (e) => {
    e.preventDefault();
    setSavingOcc(true);
    api
      .put(`/audit-occurrences/${occModalItem.id}`, {
        status: occModalItem.status,
        completion_date: occModalItem.completion_date || null,
        report_submitted: occModalItem.report_submitted,
      })
      .then(() => {
        refreshOccurrences();
        showToast('Occurrence updated ✓');
        setOccModalItem(null);
      })
      .finally(() => setSavingOcc(false));
  };

  // ----------------------------------------------------- Recommendations tab

  const [recommendations, setRecommendations] = useState([]);
  const [recModalOpen, setRecModalOpen] = useState(false);
  const [editingRecId, setEditingRecId] = useState(null);
  const [recForm, setRecForm] = useState(EMPTY_REC_FORM);
  const [recDeleteTarget, setRecDeleteTarget] = useState(null);
  const [savingRec, setSavingRec] = useState(false);

  const refreshRecommendations = () => {
    if (!selectedCompanyId) return Promise.resolve();
    return api.get('/audit-recommendations', { params: { company_id: selectedCompanyId } }).then((res) => setRecommendations(res.data));
  };

  const occurrenceLabel = (id) => {
    const o = occurrences.find((oc) => oc.id === id);
    if (!o) return `Occurrence #${id}`;
    const plan = planById(o.plan_id);
    return `${plan ? planLabel(plan) : 'Audit'} - due ${formatDate(o.due_date)}`;
  };

  const openAddRec = () => {
    setEditingRecId(null);
    setRecForm(EMPTY_REC_FORM);
    setRecModalOpen(true);
  };

  const openEditRec = (r) => {
    setEditingRecId(r.id);
    setRecForm({
      occurrence_id: r.occurrence_id,
      description: r.description,
      risk_rating: r.risk_rating,
      status: r.status,
      due_date: r.due_date || '',
    });
    setRecModalOpen(true);
  };

  const handleRecSubmit = (e) => {
    e.preventDefault();
    setSavingRec(true);
    const common = {
      description: recForm.description,
      risk_rating: recForm.risk_rating,
      status: recForm.status,
      due_date: recForm.due_date || null,
    };
    const request = editingRecId
      ? api.put(`/audit-recommendations/${editingRecId}`, common)
      : api.post('/audit-recommendations', { ...common, occurrence_id: Number(recForm.occurrence_id) });
    request
      .then(() => {
        refreshRecommendations();
        showToast(editingRecId ? 'Recommendation updated ✓' : 'Recommendation added ✓');
        setRecModalOpen(false);
      })
      .catch((err) => showToast(err.response?.data?.detail || 'Could not save recommendation'))
      .finally(() => setSavingRec(false));
  };

  const confirmDeleteRec = () => {
    if (!recDeleteTarget) return;
    api.delete(`/audit-recommendations/${recDeleteTarget.id}`).then(() => {
      refreshRecommendations();
      showToast('Recommendation deleted');
      setRecDeleteTarget(null);
    });
  };

  // ----------------------------------------- Recommendation count summary
  // (manually-entered headline numbers per audit report - separate from the
  // individual recommendation rows above, since a firm may not want to log
  // every finding as its own record and just wants the report's numbers
  // tracked as work progresses)

  const [recSummaryDraft, setRecSummaryDraft] = useState({});
  const [savingRecSummary, setSavingRecSummary] = useState(null);

  const recSummaryValue = (occ, field) => {
    const draft = recSummaryDraft[occ.id];
    if (draft && field in draft) return draft[field];
    return occ[field] ?? '';
  };

  const setRecSummaryField = (occId, field, value) => {
    setRecSummaryDraft((prev) => ({ ...prev, [occId]: { ...prev[occId], [field]: value } }));
  };

  const saveRecSummary = (occ) => {
    const toNum = (v) => (v === '' || v === undefined || v === null ? null : Number(v));
    const payload = {
      recommendations_given: toNum(recSummaryValue(occ, 'recommendations_given')),
      recommendations_closed: toNum(recSummaryValue(occ, 'recommendations_closed')),
      recommendations_open: toNum(recSummaryValue(occ, 'recommendations_open')),
      recommendations_in_process: toNum(recSummaryValue(occ, 'recommendations_in_process')),
    };
    setSavingRecSummary(occ.id);
    api
      .put(`/audit-occurrences/${occ.id}`, payload)
      .then(() => {
        refreshOccurrences();
        setRecSummaryDraft((prev) => {
          const next = { ...prev };
          delete next[occ.id];
          return next;
        });
        showToast('Recommendation counts updated ✓');
      })
      .finally(() => setSavingRecSummary(null));
  };

  // ------------------------------------------------------------------ load

  useEffect(() => {
    if (!selectedCompanyId) return;
    setLoading(true);
    Promise.all([
      refresh(),
      api.get('/clients', { params: { company_id: selectedCompanyId } }).then((res) => setClients(res.data)),
      refreshPlans(),
      refreshOccurrences(''),
      refreshRecommendations(),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  useEffect(() => {
    if (activeTab === 'occurrences') refreshOccurrences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [occFilterPlan, activeTab]);

  return (
    <PageLayout>
      <div className="page-title-row">
        <h1 className="page-title">Audit</h1>
        <p className="page-subtitle">Engagement planning, occurrences, and findings tracking</p>
      </div>

      {toast && <div className="cal-toast">{toast}</div>}

      <div className="de-tabs">
        <button type="button" className={`de-tab ${activeTab === 'log' ? 'active' : ''}`} onClick={() => setActiveTab('log')}>
          Status Log
        </button>
        <button type="button" className={`de-tab ${activeTab === 'plans' ? 'active' : ''}`} onClick={() => setActiveTab('plans')}>
          Audit Plans
        </button>
        <button type="button" className={`de-tab ${activeTab === 'occurrences' ? 'active' : ''}`} onClick={() => setActiveTab('occurrences')}>
          Occurrences
        </button>
        <button type="button" className={`de-tab ${activeTab === 'recommendations' ? 'active' : ''}`} onClick={() => setActiveTab('recommendations')}>
          Recommendations
        </button>
      </div>

      {activeTab === 'log' && (
        <div className="dash-card">
          <div className="page-title-row">
            <h2 className="dash-card-title">Audit Engagements</h2>
            <div className="table-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setImportOpen(true)}>
                Import
              </button>
              <button className="dash-btn dash-btn-orange" type="button" onClick={openAdd}>
                + Add New Audit
              </button>
            </div>
          </div>

          <table className="dash-table">
            <thead>
              <tr>
                {!isBusinessOwner && <th>Client</th>}
                <th>Type of Entity</th>
                <th>Audit Date</th>
                <th>Status</th>
                <th>Fees Received</th>
                <th>Remarks</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td className="dash-empty" colSpan={isBusinessOwner ? 6 : 7}>No audit records yet.</td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id}>
                  {!isBusinessOwner && <td>{clientName(r.client_id)}</td>}
                  <td>{clientType(r.client_id) || r.name_of_company}</td>
                  <td>{r.date_of_audit || '—'}</td>
                  <td>
                    <span className={`dash-pill ${STATUS_PILL[r.status] || 'dash-pill-blue'}`}>{r.status}</span>
                  </td>
                  <td>₹{Number(r.fees_received).toLocaleString('en-IN')}</td>
                  <td className="remarks-cell" title={r.remarks || ''}>{r.remarks || '—'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="dash-btn cal-btn-grey" type="button" onClick={() => openEdit(r)}>
                        Edit
                      </button>
                      <button className="dash-btn-red-outline" type="button" onClick={() => setDeleteTarget(r)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'plans' && (
        <div className="dash-card">
          <div className="page-title-row">
            <h2 className="dash-card-title">Audit Plans</h2>
            <button className="dash-btn dash-btn-orange" type="button" onClick={openAddPlan}>
              + Add Plan
            </button>
          </div>
          <p className="dash-table-sub" style={{ marginBottom: 16 }}>
            Adding a plan automatically generates its Occurrences for the whole FY (see the Occurrences tab).
          </p>

          <table className="dash-table">
            <thead>
              <tr>
                {!isBusinessOwner && <th>Client</th>}
                <th>FY</th>
                <th>Type</th>
                <th>Frequency</th>
                <th>Auditor</th>
                <th>Remarks</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && plans.length === 0 && (
                <tr>
                  <td className="dash-empty" colSpan={isBusinessOwner ? 6 : 7}>No audit plans yet.</td>
                </tr>
              )}
              {plans.map((p) => (
                <tr key={p.id}>
                  {!isBusinessOwner && <td>{clientName(p.client_id)}</td>}
                  <td>FY{p.fy_year}-{String(p.fy_year + 1).slice(2)}</td>
                  <td>{p.audit_type}</td>
                  <td>{p.frequency}{p.frequency === 'Other' ? ` (×${p.custom_count})` : ''}</td>
                  <td>{p.auditor_name || '—'}</td>
                  <td className="remarks-cell" title={p.remarks || ''}>{p.remarks || '—'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="dash-btn cal-btn-grey" type="button" onClick={() => openEditPlan(p)}>
                        Edit
                      </button>
                      <button className="dash-btn-red-outline" type="button" onClick={() => setPlanDeleteTarget(p)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'occurrences' && (
        <div className="dash-card">
          <div className="page-title-row">
            <h2 className="dash-card-title">Audit Occurrences</h2>
            <div className="field-group" style={{ marginBottom: 0, minWidth: 260 }}>
              <select className="field-select" value={occFilterPlan} onChange={(e) => setOccFilterPlan(e.target.value)}>
                <option value="">All plans</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>{planLabel(p)}</option>
                ))}
              </select>
            </div>
          </div>

          <table className="dash-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Due Date</th>
                <th>Status</th>
                <th>Completion Date</th>
                <th>Report Submitted</th>
                <th>Recommendations</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && occurrences.length === 0 && (
                <tr>
                  <td className="dash-empty" colSpan={7}>No occurrences yet - add an Audit Plan to generate them.</td>
                </tr>
              )}
              {occurrences.map((o) => {
                const plan = planById(o.plan_id);
                const rc = recCountsForOccurrence(o);
                return (
                  <tr key={o.id}>
                    <td>{plan ? planLabel(plan) : `Plan #${o.plan_id}`}</td>
                    <td>{formatDate(o.due_date)}</td>
                    <td>
                      <span className={`dash-pill ${OCC_STATUS_PILL[o.status] || 'dash-pill-blue'}`}>{o.status}</span>
                    </td>
                    <td>{formatDate(o.completion_date)}</td>
                    <td>{o.report_submitted ? 'Yes' : 'No'}</td>
                    <td>
                      {!rc ? (
                        <span className="dash-table-sub">None yet</span>
                      ) : (
                        <div className="table-actions" style={{ flexWrap: 'wrap', gap: 4 }}>
                          <span className="dash-pill dash-pill-blue" title="Total recommendations">{rc.total} total</span>
                          {rc.pending > 0 && <span className="dash-pill dash-pill-amber" title="Open">{rc.pending} Open</span>}
                          {rc.inProcess > 0 && <span className="dash-pill dash-pill-blue" title="In Process">{rc.inProcess} In Process</span>}
                          {rc.closed > 0 && <span className="dash-pill dash-pill-green" title="Closed">{rc.closed} Closed</span>}
                        </div>
                      )}
                    </td>
                    <td>
                      <button className="dash-btn cal-btn-grey" type="button" onClick={() => openEditOcc(o)}>
                        Update
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'recommendations' && (
        <>
        <div className="dash-card">
          <div className="page-title-row">
            <h2 className="dash-card-title">Recommendation Counts by Audit Report</h2>
          </div>
          <p className="dash-table-sub" style={{ marginBottom: 16 }}>
            Enter the auditor's headline numbers directly, then update Closed / Open / In Process as findings get worked through.
          </p>
          <table className="dash-table">
            <thead>
              <tr>
                <th>Audit Report (Occurrence)</th>
                <th>Given by Auditor</th>
                <th>Closed</th>
                <th>Open</th>
                <th>In Process</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && occurrences.length === 0 && (
                <tr>
                  <td className="dash-empty" colSpan={6}>No audit reports yet - add an Audit Plan first.</td>
                </tr>
              )}
              {occurrences.map((o) => {
                const given = recSummaryValue(o, 'recommendations_given');
                const closed = recSummaryValue(o, 'recommendations_closed');
                const open = recSummaryValue(o, 'recommendations_open');
                const inProcess = recSummaryValue(o, 'recommendations_in_process');
                const anyCountEntered = closed !== '' || open !== '' || inProcess !== '';
                const countTotal = anyCountEntered ? (Number(closed) || 0) + (Number(open) || 0) + (Number(inProcess) || 0) : null;
                const mismatch = given !== '' && countTotal !== null && Number(given) !== countTotal;
                const plan = planById(o.plan_id);
                const isDirty = !!recSummaryDraft[o.id];
                return (
                  <tr key={o.id}>
                    <td>
                      {plan ? planLabel(plan) : 'Audit'} - due {formatDate(o.due_date)}
                      {mismatch && (
                        <div className="dash-table-sub" style={{ color: '#d97706' }}>
                          Closed+Open+In Process ({countTotal}) does not match Given ({given})
                        </div>
                      )}
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        className="field-input"
                        style={{ width: 80 }}
                        value={given}
                        onChange={(e) => setRecSummaryField(o.id, 'recommendations_given', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        className="field-input"
                        style={{ width: 80 }}
                        value={closed}
                        onChange={(e) => setRecSummaryField(o.id, 'recommendations_closed', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        className="field-input"
                        style={{ width: 80 }}
                        value={open}
                        onChange={(e) => setRecSummaryField(o.id, 'recommendations_open', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        className="field-input"
                        style={{ width: 80 }}
                        value={inProcess}
                        onChange={(e) => setRecSummaryField(o.id, 'recommendations_in_process', e.target.value)}
                      />
                    </td>
                    <td>
                      <button
                        className="dash-btn dash-btn-orange"
                        type="button"
                        disabled={!isDirty || savingRecSummary === o.id}
                        onClick={() => saveRecSummary(o)}
                      >
                        {savingRecSummary === o.id ? 'Saving...' : 'Save'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="dash-card">
          <div className="page-title-row">
            <h2 className="dash-card-title">Audit Recommendations</h2>
            <button className="dash-btn dash-btn-orange" type="button" onClick={openAddRec} disabled={occurrences.length === 0}>
              + Add Recommendation
            </button>
          </div>
          {occurrences.length === 0 && (
            <p className="dash-table-sub" style={{ marginBottom: 16 }}>Add an Audit Plan first - a recommendation attaches to a specific occurrence.</p>
          )}

          <table className="dash-table">
            <thead>
              <tr>
                <th>Occurrence</th>
                <th>Description</th>
                <th>Risk</th>
                <th>Status</th>
                <th>Due Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && recommendations.length === 0 && (
                <tr>
                  <td className="dash-empty" colSpan={6}>No recommendations yet.</td>
                </tr>
              )}
              {recommendations.map((r) => (
                <tr key={r.id}>
                  <td>{occurrenceLabel(r.occurrence_id)}</td>
                  <td className="remarks-cell" title={r.description}>{r.description}</td>
                  <td>
                    <span className={`dash-pill ${RISK_PILL[r.risk_rating] || 'dash-pill-blue'}`}>{r.risk_rating}</span>
                  </td>
                  <td>
                    <span className={`dash-pill ${REC_STATUS_PILL[r.status] || 'dash-pill-blue'}`}>{r.status}</span>
                  </td>
                  <td>{formatDate(r.due_date)}</td>
                  <td>
                    <div className="table-actions">
                      <button className="dash-btn cal-btn-grey" type="button" onClick={() => openEditRec(r)}>
                        Edit
                      </button>
                      <button className="dash-btn-red-outline" type="button" onClick={() => setRecDeleteTarget(r)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* ---- Log modal (unchanged) ---- */}
      {modalOpen && (
        <div className="ncf-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">{editingId ? 'Edit Audit Record' : 'Add New Audit'}</h3>
            <form onSubmit={handleSubmit}>
              {!isBusinessOwner && (
                <div className="field-group">
                  <span className="field-label">Client</span>
                  <select
                    className="field-select"
                    value={form.client_id}
                    onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  >
                    <option value="">Not linked to a client</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name_of_client}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="field-group">
                <span className="field-label">Type of Entity *</span>
                <input
                  className="field-input"
                  required
                  value={form.name_of_company}
                  onChange={(e) => setForm({ ...form, name_of_company: e.target.value })}
                />
              </div>
              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">Date of Audit</span>
                  <input
                    type="date"
                    className="field-input"
                    value={form.date_of_audit || ''}
                    onChange={(e) => setForm({ ...form, date_of_audit: e.target.value })}
                  />
                </div>
                <div className="field-group">
                  <span className="field-label">Status</span>
                  <select
                    className="field-select"
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="field-group">
                <span className="field-label">Fees Received</span>
                <input
                  type="number"
                  className="field-input"
                  value={form.fees_received}
                  onChange={(e) => setForm({ ...form, fees_received: e.target.value })}
                />
              </div>
              <div className="field-group">
                <span className="field-label">Remarks</span>
                <input
                  className="field-input"
                  value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                />
              </div>
              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={() => setModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-orange" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="ncf-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Delete Audit Record</h3>
            <p style={{ fontSize: 14, color: '#374151' }}>
              Are you sure you want to delete the audit record for <strong>{deleteTarget.name_of_company}</strong>? This cannot be undone.
            </p>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button className="dash-btn dash-btn-orange" style={{ background: '#dc2626' }} type="button" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Plan modal ---- */}
      {planModalOpen && (
        <div className="ncf-modal-overlay" onClick={() => setPlanModalOpen(false)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">{editingPlanId ? 'Edit Audit Plan' : 'Add Audit Plan'}</h3>
            <form onSubmit={handlePlanSubmit}>
              {!isBusinessOwner && (
                <div className="field-group">
                  <span className="field-label">Client</span>
                  <select
                    className="field-select"
                    value={planForm.client_id}
                    onChange={(e) => setPlanForm({ ...planForm, client_id: e.target.value })}
                  >
                    <option value="">Not linked to a client</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name_of_client}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">FY Start Year *</span>
                  <input
                    type="number"
                    className="field-input"
                    required
                    value={planForm.fy_year}
                    onChange={(e) => setPlanForm({ ...planForm, fy_year: e.target.value })}
                  />
                </div>
                <div className="field-group">
                  <span className="field-label">Audit Type *</span>
                  <select
                    className="field-select"
                    value={planForm.audit_type}
                    onChange={(e) => setPlanForm({ ...planForm, audit_type: e.target.value })}
                  >
                    {AUDIT_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">Frequency *</span>
                  <select
                    className="field-select"
                    value={planForm.frequency}
                    onChange={(e) => setPlanForm({ ...planForm, frequency: e.target.value })}
                  >
                    {FREQUENCIES.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
                {planForm.frequency === 'Other' && (
                  <div className="field-group">
                    <span className="field-label">Occurrences per FY *</span>
                    <input
                      type="number"
                      min="1"
                      className="field-input"
                      required
                      value={planForm.custom_count}
                      onChange={(e) => setPlanForm({ ...planForm, custom_count: e.target.value })}
                    />
                  </div>
                )}
              </div>
              <div className="field-group">
                <span className="field-label">Auditor Name</span>
                <input
                  className="field-input"
                  value={planForm.auditor_name}
                  onChange={(e) => setPlanForm({ ...planForm, auditor_name: e.target.value })}
                />
              </div>
              <div className="field-group">
                <span className="field-label">Remarks</span>
                <input
                  className="field-input"
                  value={planForm.remarks}
                  onChange={(e) => setPlanForm({ ...planForm, remarks: e.target.value })}
                />
              </div>
              {!editingPlanId && (
                <p className="dash-table-sub">Saving will generate this plan's occurrences for the whole FY automatically.</p>
              )}
              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={() => setPlanModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-orange" disabled={savingPlan}>
                  {savingPlan ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {planDeleteTarget && (
        <div className="ncf-modal-overlay" onClick={() => setPlanDeleteTarget(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Delete Audit Plan</h3>
            <p style={{ fontSize: 14, color: '#374151' }}>
              Delete the <strong>{planLabel(planDeleteTarget)}</strong> plan? This also deletes all of its Occurrences and any
              Recommendations attached to them. This cannot be undone.
            </p>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setPlanDeleteTarget(null)}>
                Cancel
              </button>
              <button className="dash-btn dash-btn-orange" style={{ background: '#dc2626' }} type="button" onClick={confirmDeletePlan}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Occurrence update modal ---- */}
      {occModalItem && (
        <div className="ncf-modal-overlay" onClick={() => setOccModalItem(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Update Occurrence</h3>
            <form onSubmit={handleOccSubmit}>
              <div className="field-group">
                <span className="field-label">Status</span>
                <select
                  className="field-select"
                  value={occModalItem.status}
                  onChange={(e) => setOccModalItem({ ...occModalItem, status: e.target.value })}
                >
                  {OCCURRENCE_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="field-group">
                <span className="field-label">Completion Date</span>
                <input
                  type="date"
                  className="field-input"
                  value={occModalItem.completion_date}
                  onChange={(e) => setOccModalItem({ ...occModalItem, completion_date: e.target.value })}
                />
              </div>
              <label className="checkbox-item" style={{ marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={occModalItem.report_submitted}
                  onChange={(e) => setOccModalItem({ ...occModalItem, report_submitted: e.target.checked })}
                />
                Report Submitted
              </label>
              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={() => setOccModalItem(null)}>
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-orange" disabled={savingOcc}>
                  {savingOcc ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---- Recommendation modal ---- */}
      {recModalOpen && (
        <div className="ncf-modal-overlay" onClick={() => setRecModalOpen(false)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">{editingRecId ? 'Edit Recommendation' : 'Add Recommendation'}</h3>
            <form onSubmit={handleRecSubmit}>
              {!editingRecId && (
                <div className="field-group">
                  <span className="field-label">Occurrence *</span>
                  <select
                    className="field-select"
                    required
                    value={recForm.occurrence_id}
                    onChange={(e) => setRecForm({ ...recForm, occurrence_id: e.target.value })}
                  >
                    <option value="">Select an occurrence</option>
                    {occurrences.map((o) => {
                      const plan = planById(o.plan_id);
                      return (
                        <option key={o.id} value={o.id}>
                          {plan ? planLabel(plan) : 'Audit'} - due {formatDate(o.due_date)}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
              <div className="field-group">
                <span className="field-label">Description *</span>
                <input
                  className="field-input"
                  required
                  value={recForm.description}
                  onChange={(e) => setRecForm({ ...recForm, description: e.target.value })}
                />
              </div>
              <div className="field-row">
                <div className="field-group">
                  <span className="field-label">Risk Rating</span>
                  <select
                    className="field-select"
                    value={recForm.risk_rating}
                    onChange={(e) => setRecForm({ ...recForm, risk_rating: e.target.value })}
                  >
                    {RISK_RATINGS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div className="field-group">
                  <span className="field-label">Status</span>
                  <select
                    className="field-select"
                    value={recForm.status}
                    onChange={(e) => setRecForm({ ...recForm, status: e.target.value })}
                  >
                    {RECOMMENDATION_STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="field-group">
                <span className="field-label">Due Date</span>
                <input
                  type="date"
                  className="field-input"
                  value={recForm.due_date}
                  onChange={(e) => setRecForm({ ...recForm, due_date: e.target.value })}
                />
              </div>
              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={() => setRecModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-orange" disabled={savingRec}>
                  {savingRec ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {recDeleteTarget && (
        <div className="ncf-modal-overlay" onClick={() => setRecDeleteTarget(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Delete Recommendation</h3>
            <p style={{ fontSize: 14, color: '#374151' }}>Delete this recommendation? This cannot be undone.</p>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setRecDeleteTarget(null)}>
                Cancel
              </button>
              <button className="dash-btn dash-btn-orange" style={{ background: '#dc2626' }} type="button" onClick={confirmDeleteRec}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        module="audit"
        moduleLabel="Audit Status"
        companyId={selectedCompanyId}
        onImported={refresh}
        supportsCreateNew
      />
    </PageLayout>
  );
}

export default AuditStatus;
